/**
 * LineryMerch — moteur de calcul tarifaire
 * ----------------------------------------
 * Fonction pure, sans DOM : elle prend une configuration et rend un devis.
 * Isolée volontairement du composant d'interface, pour deux raisons :
 *   1. elle est testable seule (voir linery-pricing.test.js) ;
 *   2. le jour où la fonction Shopify de remise produit sera déployée,
 *      c'est exactement cette logique qu'elle devra reproduire côté serveur.
 *
 * Toutes les sommes sont manipulées en centimes entiers. Aucun flottant
 * n'intervient dans un calcul de monnaie : 0.1 + 0.2 ne vaut pas 0.3.
 *
 * Règles métier (arrêtées en réunion des 27/07 et 17/08/2026) :
 *   - MOQ 30 pièces, toutes tailles confondues ;
 *   - six paliers : 30, 50, 100, 250, 500, 1000 ;
 *   - un palier est un seuil : 137 pièces sont facturées au tarif du palier 100 ;
 *   - le prix dépend du produit, du grammage et de la quantité totale — pas de la taille ;
 *   - le label est un produit complémentaire, avec sa propre grille sur les mêmes paliers.
 */
(function (global) {
  'use strict';

  var toCents = function (eur) { return Math.round(Number(eur) * 100); };

  function LineryPricing(data) {
    if (!data || !data.products) throw new Error('LineryPricing : données tarifaires absentes ou invalides');
    this.raw = data;
    this.moq = data.moq || 30;
    this.tiers = (data.tiers || []).slice().sort(function (a, b) { return a - b; });
    this.labelPrices = {};
    var lp = (data.labels && data.labels.prices) || {};
    for (var t in lp) if (Object.prototype.hasOwnProperty.call(lp, t)) this.labelPrices[t] = toCents(lp[t]);
  }

  /** La fiche d'un couple produit / grammage, ou null. */
  LineryPricing.prototype.entry = function (productKey, gsm) {
    var p = this.raw.products[String(productKey || '').toLowerCase()];
    if (!p) return null;
    var keys = Object.keys(p.gsm);
    if (!keys.length) return null;
    // Grammage absent ou non reconnu : on retombe sur le seul disponible.
    var g = (gsm === null || gsm === undefined || !p.gsm[String(gsm)]) ? keys[0] : String(gsm);
    return { product: p, gsm: g, data: p.gsm[g] };
  };

  /** Le palier applicable à une quantité. En dessous du MOQ, le premier palier sert de référence indicative. */
  LineryPricing.prototype.tierFor = function (qty) {
    var applicable = this.tiers[0];
    for (var i = 0; i < this.tiers.length; i++) if (qty >= this.tiers[i]) applicable = this.tiers[i];
    return applicable;
  };

  /** Le palier immédiatement supérieur, ou null si l'on est déjà au dernier. */
  LineryPricing.prototype.nextTierAbove = function (qty) {
    for (var i = 0; i < this.tiers.length; i++) if (this.tiers[i] > qty) return this.tiers[i];
    return null;
  };

  /** Prix unitaire en centimes pour un produit / grammage / quantité. */
  LineryPricing.prototype.unitPriceCents = function (productKey, gsm, qty) {
    var e = this.entry(productKey, gsm);
    if (!e) return null;
    var v = e.data.prices[String(this.tierFor(qty))];
    return v === undefined ? null : toCents(v);
  };

  /**
   * Le devis complet.
   *
   * L'économie d'échelle se calcule **par configuration** — produit, grammage et
   * couleur — toutes tailles confondues. Règle de Lohan du 18/08/2026 : trente
   * t-shirts blancs 190 GSM ouvrent leur propre palier ; trente noirs dans la
   * même commande ouvrent le leur, sans s'additionner aux blancs.
   *
   * D'où `inCart` : la quantité déjà au panier pour cette même configuration.
   * Le palier, le minimum de commande et l'incitation portent sur le total de
   * la configuration ; le sous-total, lui, ne porte que sur ce qu'on ajoute.
   *
   * @param {Object} cfg
   * @param {string} cfg.product   clé produit ('core', 'fold'…)
   * @param {string|number} cfg.gsm
   * @param {Object} cfg.sizes     { XS: 0, S: 10, M: 20… }
   * @param {boolean} cfg.label    étiquette commandée en complément
   * @param {number} cfg.inCart    déjà au panier dans cette configuration
   * @returns {Object} devis
   */
  LineryPricing.prototype.quote = function (cfg) {
    cfg = cfg || {};
    var e = this.entry(cfg.product, cfg.gsm);
    var sizes = cfg.sizes || {};

    var qty = 0, lines = [];
    for (var s in sizes) {
      if (!Object.prototype.hasOwnProperty.call(sizes, s)) continue;
      var n = parseInt(sizes[s], 10);
      if (!isFinite(n) || n <= 0) continue;
      qty += n;
      lines.push({ size: s, quantity: n });
    }

    var inCart = Math.max(0, parseInt(cfg.inCart, 10) || 0);
    var tierQty = qty + inCart;

    var q = {
      ok: false,
      quantity: qty,
      inCart: inCart,
      tierQuantity: tierQty,
      lines: lines,
      meetsMoq: tierQty >= this.moq,
      moq: this.moq,
      missingForMoq: Math.max(0, this.moq - tierQty),
      tier: null,
      unitPrice: 0,
      subtotal: 0,
      label: { enabled: !!cfg.label, unitPrice: 0, subtotal: 0 },
      total: 0,
      savePercent: 0,
      saveAmount: 0,
      next: null,
      error: null,
    };

    if (!e) { q.error = 'unknown_product'; return q; }

    q.tier = this.tierFor(tierQty);
    var unit = e.data.prices[String(q.tier)];
    if (unit === undefined) { q.error = 'missing_tier'; return q; }

    q.ok = true;
    q.unitPrice = toCents(unit);
    q.subtotal = q.unitPrice * qty;

    if (cfg.label) {
      q.label.unitPrice = this.labelPrices[String(q.tier)] || 0;
      q.label.subtotal = q.label.unitPrice * qty;
    }
    q.total = q.subtotal + q.label.subtotal;

    // Économie mesurée par rapport au tarif d'entrée, sur ce qu'on ajoute.
    var basePrice = e.data.prices[String(this.tiers[0])];
    if (basePrice !== undefined) {
      var base = toCents(basePrice);
      q.saveAmount = Math.max(0, (base - q.unitPrice) * qty);
      q.savePercent = base > 0 ? Math.round((base - q.unitPrice) / base * 1000) / 10 : 0;
    }

    // L'incitation au palier suivant, mesurée sur la configuration entière.
    var nt = this.nextTierAbove(tierQty);
    if (nt !== null && e.data.prices[String(nt)] !== undefined) {
      var ntUnit = toCents(e.data.prices[String(nt)]);
      var ntLabel = cfg.label ? (this.labelPrices[String(nt)] || 0) : 0;
      var here = (q.unitPrice + q.label.unitPrice) * tierQty;
      var ntTotal = (ntUnit + ntLabel) * nt;
      q.next = {
        tier: nt,
        add: nt - tierQty,
        unitPrice: ntUnit,
        unitSaving: q.unitPrice - ntUnit,
        total: ntTotal,
        // Vrai dans presque tous les cas : commander plus coûte moins cher au total.
        cheaperOverall: ntTotal < here,
      };
    }

    return q;
  };

  /** Les couleurs disponibles pour un grammage donné, résolues en objets complets. */
  LineryPricing.prototype.colorsFor = function (productKey, gsm) {
    var e = this.entry(productKey, gsm);
    if (!e) return [];
    var all = this.raw.colors || {};
    var out = [];
    for (var i = 0; i < e.data.colors.length; i++) {
      var code = e.data.colors[i];
      if (all[code]) out.push(Object.assign({ code: code }, all[code]));
    }
    return out;
  };

  /** Le prix d'un échantillon, en centimes. */
  LineryPricing.prototype.sampleCents = function (productKey, gsm) {
    var e = this.entry(productKey, gsm);
    return e ? toCents(e.data.sample) : null;
  };

  LineryPricing.prototype.gsmList = function (productKey) {
    var p = this.raw.products[String(productKey || '').toLowerCase()];
    return p ? Object.keys(p.gsm).map(Number).sort(function (a, b) { return a - b; }) : [];
  };

  LineryPricing.toCents = toCents;

  /**
   * Tarifs par type de client (07/10/2026). Le badge de la fiche client
   * (« prix 3 » Agency, « prix 2 » à venir) choisit une grille ; le thème la sort dans
   * la page, pour ce seul client, sous `#linery-tarif-data`. Cette fonction en
   * reporte les prix (l'échantillon et le barème des étiquettes s'ils sont
   * donnés) sur les données de la grille standard. Un couple produit / grammage absent du tarif garde son
   * prix standard, comme dans la fonction de remise du panier. Rend une copie.
   */
  LineryPricing.appliquerTarif = function (data, tarif) {
    if (!data || !data.products || !tarif || !tarif.products) return data;
    var copie = JSON.parse(JSON.stringify(data));
    for (var pk in tarif.products) {
      if (!Object.prototype.hasOwnProperty.call(tarif.products, pk)) continue;
      var produit = copie.products[pk];
      if (!produit || !produit.gsm) continue;
      var lignes = tarif.products[pk];
      for (var g in lignes) {
        if (!Object.prototype.hasOwnProperty.call(lignes, g) || !produit.gsm[g]) continue;
        var ligne = lignes[g] || {};
        if (ligne.prices) {
          for (var t in ligne.prices) {
            if (Object.prototype.hasOwnProperty.call(ligne.prices, t)) produit.gsm[g].prices[t] = ligne.prices[t];
          }
        }
        if (typeof ligne.sample === 'number') produit.gsm[g].sample = ligne.sample;
      }
    }
    // Bareme des etiquettes propre au tarif, s'il en a un ; sinon le standard.
    if (tarif.labels && tarif.labels.prices && copie.labels) {
      copie.labels.prices = copie.labels.prices || {};
      for (var lt in tarif.labels.prices) {
        if (Object.prototype.hasOwnProperty.call(tarif.labels.prices, lt)) copie.labels.prices[lt] = tarif.labels.prices[lt];
      }
    }
    copie.tarif = tarif.tarif || null;
    return copie;
  };

  global.LineryPricing = LineryPricing;
}(typeof window !== 'undefined' ? window : globalThis));
